import useNavBarStore, { isPanelSectionPath, resolvePanelForPath } from '@/stores/nav-store';
import { Box } from '@chakra-ui/react';
import { useReducedMotion } from 'framer-motion';
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { TOP_BAR_HEIGHT } from '../layout/TopBar';
import { useIsMobileLayout } from '../layout/nav-dark';
import MainNav from './MainNav';
import ConversationsMenu from './sidebar-views/ConversationsMenu';
import ProgramMenu from './sidebar-views/ProgramMenu';
import SettingsMenu from './sidebar-views/SettingsMenu';

/** Single fixed sidebar width. Content margin in Layout keeps in sync. */
export const SIDEBAR_WIDTH = 250;

const PrimarySidebar: React.FC<{ bannerHeight?: number }> = ({ bannerHeight }) => {
  const isMobile = useIsMobileLayout();
  const reduceMotion = useReducedMotion() ?? false;
  const location = useLocation();
  const navigate = useNavigate();

  const activePanel = useNavBarStore((state) => state.activePanel);
  const setActivePanel = useNavBarStore((state) => state.setActivePanel);
  const clearData = useNavBarStore((state) => state.clearData);

  // Keep the last non-null panel mounted while it slides out, so the exit
  // animation reads before it unmounts.
  const [displayedPanel, setDisplayedPanel] = useState(activePanel);
  useEffect(() => {
    if (activePanel) setDisplayedPanel(activePanel);
  }, [activePanel]);

  // The active panel is derived from the URL. Settings / inbox / a specific
  // program each own an internal panel; everything else shows the main nav.
  useEffect(() => {
    setActivePanel(resolvePanelForPath(location.pathname));
  }, [location.pathname, setActivePanel]);

  const handleBack = useCallback(() => {
    const { previousPath } = useNavBarStore.getState();
    clearData();
    if (previousPath && !isPanelSectionPath(previousPath)) {
      navigate(previousPath);
    } else {
      navigate('/dashboard');
    }
  }, [navigate, clearData]);

  if (isMobile) {
    return null;
  }

  const isPanelOpen = activePanel !== null;

  const renderPanel = () => {
    switch (displayedPanel) {
      case 'settings':
        return <SettingsMenu onBack={handleBack} />;
      case 'conversations':
        return <ConversationsMenu onBack={handleBack} />;
      case 'program':
        return <ProgramMenu onBack={handleBack} />;
      default:
        return null;
    }
  };

  const panelTransition = reduceMotion
    ? 'opacity .2s ease'
    : 'transform .34s cubic-bezier(.22,1,.36,1), opacity .22s ease';

  return (
    <Box
      position="fixed"
      left={0}
      top={`${TOP_BAR_HEIGHT + (bannerHeight ?? 0)}px`}
      bottom={0}
      w={`${SIDEBAR_WIDTH}px`}
      // Transparent on the frame NavFrameBackdrop paints, so the menu and the
      // top bar read as one surface with no seam.
      color="navDark.text"
      // Loading placeholders of the panels (conversations, sent mail, program
      // title) pulse on `bg.emphasized`, a light grey drawn for white pages:
      // redefined here once, for everything the menu holds.
      css={{ '--chakra-colors-bg-emphasized': 'var(--chakra-colors-nav-dark-skeleton)' }}
      zIndex="sticky"
      overflow="hidden"
    >
      <Box
        position="absolute"
        inset={0}
        style={{
          transform: isPanelOpen && !reduceMotion ? 'translateX(-28px)' : 'translateX(0)',
          opacity: isPanelOpen ? 0 : 1,
          pointerEvents: isPanelOpen ? 'none' : 'auto',
          transition: panelTransition,
        }}
      >
        <MainNav />
      </Box>

      <Box
        position="absolute"
        inset={0}
        onTransitionEnd={(e: React.TransitionEvent) => {
          // transitionend bubbles from panel children; only the container's own
          // transition marks the end of the slide-out.
          if (e.target !== e.currentTarget) return;
          if (!isPanelOpen) setDisplayedPanel(null);
        }}
        style={{
          transform: isPanelOpen || reduceMotion ? 'translateX(0)' : 'translateX(28px)',
          opacity: isPanelOpen ? 1 : 0,
          pointerEvents: isPanelOpen ? 'auto' : 'none',
          transition: panelTransition,
        }}
      >
        {renderPanel()}
      </Box>
    </Box>
  );
};

export default PrimarySidebar;
